import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import EditMultiplePropagationConstraintForm from './EditMultiplePropagationConstraintForm';
import Path from './Path';
import { Bin, AddPlus, Edit } from '../Icons';
import IconButton from '../IconButton';
import { SYMBOLS } from '../../stores/model/metamodel/mpc_config';
import { constraintDisablesPath } from '../../stores/model/metamodel/mpc_utils';
import {
  TemporaryPath,
  EditedConstraint,
  type IPathElement,
  type MPCChangeSet,
  type ConstraintKey,
  type PathsByObjectType,
  type DisplayPath,
} from './mpcModels';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IMultiplePropagationConstraint } from '../../stores/model/metamodel/multiple_propagation_constraint';
import type { IMultiplePropagationConstraintInspector } from '../../stores/ui/multiple_propagation_constraint_inspector';
import type EditObjectController from '../../merode/controllers/EditObjectController';

/** The `MPCInspector` ui store (aliased for brevity). */
export type MPCInspectorUI = IMultiplePropagationConstraintInspector;

/** Props for the {@link PathAccessorChain} component. */
interface PathAccessorChainProps {
  /** The class to put on the wrapper div. */
  className?: string;
  /** The path to render. */
  path?: DisplayPath;
}

/**
 * Render a path as an accessor chain. E.g.: self.parent.grandparent.
 *
 * Props:
 * - className: the class to put on the wrapper div
 * - path: the path to render
 */
const PathAccessorChain = ({ className, path }: PathAccessorChainProps) => {
  if (path != null) {
    return (
      <div className={className}>
        <span>self</span>
        {/* `accessorChain` holds only dependency edges (filtered), so each
            carries the `name.master` role read here. */}
        {(path.accessorChain as IDependency[]).map((e) => (
          <span key={e.id}>.{e.name.master}</span>
        ))}
      </div>
    );
  }
};

// Helper function that wraps an event handler and stops propagation of the
// event before calling the wrapped handler.
function stopPropagation(cb: () => void) {
  return function (e: React.MouseEvent) {
    e.stopPropagation();
    cb();
  };
}

/**
 * Custom hooks that provides handlers for MPC actions.
 *
 * Also discards the current edit when the component using this hook is
 * unmounted.
 *
 * @param ui The MPCInspectorUI
 *
 * @return An object with:
 * - onDelete: delete the MPC with the given id
 * - onUndoDelete: undo the delete of the MPC with the given id
 * - onStartEdit: start editing the MPC with the given id
 * - onStopEdit: stop editing the MPC with the given id
 * - onAddMPC: add a new MPC
 * - onCancelAddMPC: cancel adding an MPC
 * - onChangeMPC: generic change handler for MPC properties. Args:
 *   - id: the id of the MPC to change
 *   - key: the key of the property to change
 *   - original: the original value for the property
 *   - changed: the new value for the property
 */
function useMPCActions(ui: MPCInspectorUI) {
  function onDelete(id: string) {
    ui.changes.remove(id);
  }

  function onUndoDelete(id: string) {
    ui.changes.undoRemove(id);
  }

  function onStartEdit(id: string, adding = false) {
    ui.startEdit(id, adding);
    ui.select(id);
  }

  function onStopEdit(id?: string) {
    // The store's `select` is typed for a string id, but the original clears the
    // selection by passing `null` (the action sets `selected = null`); and
    // `stopEdit` ignores any argument. Preserve both calls verbatim via casts.
    // `id` is optional: the edit form forwards this as `onRequestClose` and
    // invokes it with no argument.
    ui.select(null as unknown as string);
    (ui.stopEdit as (id?: string) => void)(id);
  }

  function onAddMPC() {
    const id = ui.changes.addMPC();
    onStartEdit(id, true);
  }

  function discardEditing() {
    ui.discardEditing();
  }

  function onCancelAddMPC() {
    discardEditing();
    ui.changes.undoAddMPC();
  }

  function onChangeMPC(
    id: string,
    key: ConstraintKey,
    original: string | undefined,
    changed: string | undefined
  ) {
    ui.changes.storeChange(id, key as Exclude<ConstraintKey, 'id'>, original, changed);
  }

  // Discard the current edit when the component using this hook is unmounted
  useEffect(() => {
    return () => {
      discardEditing();
    };
  }, [ui]);

  return {
    onDelete,
    onUndoDelete,
    onStartEdit,
    onStopEdit,
    onAddMPC,
    onCancelAddMPC,
    onChangeMPC,
  };
}

/**
 * Custom hook that checks whether the paths have overlapping parts that will
 * be trimmed when the constraint is saved.
 *
 * @param constraint the constraint to check
 *
 * @return On object with:
 * - willTrim: true if the paths will be trimmed, false otherwise
 * - targetObject: the object where the constraint will be added after trimming
 *   the path (if willTrim is true)
 */
function usePathTrimmingCheck(constraint: EditedConstraint) {
  // When trimming applies, this hook runs on an added constraint whose primary
  // and secondary paths are both present (the form requires them). Assert them.
  const overlappingTail = (constraint.primaryPath as TemporaryPath).overlappingDependencyTail(
    (constraint.secondaryPath as TemporaryPath).elements
  );
  const result: { willTrim: boolean; targetObject: IObjectType | undefined } = {
    willTrim: false,
    targetObject: undefined,
  };
  if (overlappingTail.length > 0) {
    result.willTrim = true;
    result.targetObject = overlappingTail[0].previousObject;
  }

  return result;
}

/** Props for the {@link PathTrimmingWarning} component. */
interface PathTrimmingWarningProps {
  /** The constraint in question. */
  constraint: EditedConstraint;
}

/**
 * Render a warning when the paths of a constraint will be trimmed
 *
 * Props:
 * - constraint: the constraint in question
 */
const PathTrimmingWarning = ({ constraint }: PathTrimmingWarningProps) => {
  const { willTrim, targetObject } = usePathTrimmingCheck(constraint);
  return (
    willTrim && (
      <div className="path-trimming-warning">
        Part of the primary and secondary path are overlapping. This part will be trimmed. As a
        consequence, the constraint will be added to <strong>{targetObject!.name}</strong>.
      </div>
    )
  );
};

/**
 * Render a single multiple propagation constraint
 *
 * Props:
 * - constraint: the MPC
 * - selected: is the constraint selected?
 * - onSelect: callback to select the constraint
 * - added: is this a newly added constraint?
 * - deleted: is the constraint deleted?
 * - onDelete: callback to delete the constraint
 * - onUndoDelete: callback to undo the delete of the constraint
 * - editing: is the constraint currently being edited?
 * - onStartEdit: callback to start editing the constraint
 * - dirty: are the pending changes?
 */
/** Props for the {@link MultiplePropagationConstraint} component. */
interface MultiplePropagationConstraintProps {
  /** The (edited) MPC to render. */
  constraint: EditedConstraint;
  /** Is the constraint selected? */
  selected: boolean;
  /** Callback to select the constraint. */
  onSelect: () => void;
  /** Is this a newly added constraint? */
  added?: boolean;
  /** Is the constraint deleted? */
  deleted: boolean;
  /** Callback to delete the constraint. */
  onDelete: () => void;
  /** Callback to undo the delete of the constraint. */
  onUndoDelete: () => void;
  /** Is the constraint currently being edited? */
  editing: boolean;
  /** Callback to start editing the constraint. */
  onStartEdit: () => void;
  /** Are there pending changes? */
  dirty: boolean;
  /** The available paths (passed by the parent, unused in this child). */
  paths?: PathsByObjectType;
}

const MultiplePropagationConstraint = observer(
  ({
    constraint,
    selected,
    onSelect,
    added = false,
    deleted,
    onDelete,
    onUndoDelete,
    editing,
    onStartEdit,
    dirty,
  }: MultiplePropagationConstraintProps) => {
    const primaryPath = constraint.primaryPath;
    const secondaryPath = constraint.secondaryPath;

    const classNames = ['multiple-propagation-constraint'];
    if (editing) {
      classNames.push('editing');
    } else if (deleted) {
      classNames.push('deleted');
    }
    if (selected) {
      classNames.push('selected');
    }
    if (added) {
      classNames.push('added');
    }
    if (dirty) {
      classNames.push('dirty');
    }

    return (
      <div className={classNames.join(' ')} onClick={onSelect}>
        <div className="mpc-header">
          <div className="object">{constraint.constrainedObjectName}</div>
          {deleted ? (
            <IconButton variant="inline" onClick={stopPropagation(onUndoDelete)}>
              <AddPlus />
            </IconButton>
          ) : (
            [
              <IconButton key="edit" variant="inline" onClick={stopPropagation(onStartEdit)}>
                <Edit />
              </IconButton>,
              <IconButton key="delete" variant="inline" onClick={stopPropagation(onDelete)}>
                <Bin />
              </IconButton>,
            ]
          )}
        </div>
        <div className="constraint">
          <PathAccessorChain className="primary-path" path={primaryPath} />
          {constraint.type != null && (
            <div className="type">{SYMBOLS[constraint.type as keyof typeof SYMBOLS]}</div>
          )}
          <PathAccessorChain className="secondary-path" path={secondaryPath} />
        </div>
        {primaryPath != null && (
          <div className="path mpc-path">
            <div className="title inspector-subtitle">Primary path</div>
            <Path path={primaryPath} />
          </div>
        )}
        {secondaryPath != null && (
          <div className="path mpc-path">
            <div className="title inspector-subtitle">Secondary path</div>
            <Path path={secondaryPath} />
          </div>
        )}
        {added && <PathTrimmingWarning constraint={constraint} />}
      </div>
    );
  }
);

/**
 * Helper function to create an EditedConstraint instance for the given
 * constraint.
 */
function getEditedConstraint(
  ui: MPCInspectorUI,
  constraint: IMultiplePropagationConstraint
): EditedConstraint {
  const {
    changes: { changeSet },
  } = ui;

  // The store's `changeSet` returns its MST change instance; view it as the
  // loose `MPCChangeSet` the `EditedConstraint` reads by key.
  const cs = changeSet(constraint.id) as unknown as MPCChangeSet | undefined;
  return new EditedConstraint(constraint, cs);
}

/**
 * Custom hook that produces the list of EditedConstraint instances.
 */
function useEditedConstraints(
  ui: MPCInspectorUI,
  constraints: IMultiplePropagationConstraint[],
  paths: PathsByObjectType
): EditedConstraint[] {
  const {
    changes: { added },
  } = ui;

  return constraints
    .map((c) => getEditedConstraint(ui, c))
    .concat(
      added.map((a) => new EditedConstraint(undefined, a as unknown as MPCChangeSet, paths))
    );
}

/**
 * Custom hook that produces the available paths from the given object to all
 * other objects.
 *
 * @param controller the controller (an instance of EditObjectController)
 * @param object the object
 *
 * @return A map from object type name to the available paths.
 */
function useEditorPaths(controller: EditObjectController, object: IObjectType): PathsByObjectType {
  return useMemo(() => {
    const pathsByObjectType: PathsByObjectType = {};
    for (const ot of controller.availableObjectTypes) {
      if (ot !== object) {
        const paths = ot.pathsTo(object);
        if (paths.length > 1) {
          // `pathsTo` yields `PathEdge[][]`; each path is the `IPathElement[]`
          // the temporary path wraps.
          pathsByObjectType[ot.name] = paths.map((p) => new TemporaryPath(p as IPathElement[]));
        }
      }
    }

    return pathsByObjectType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);
}

/**
 * Custom hook that filters out the disabled paths from the given list of paths.
 *
 * @param paths the full list of paths
 * @param constraints the list of constraints
 * @param editing the constraint is currently being edited
 */
function useFilteredPaths(
  paths: PathsByObjectType,
  // Mixes real model constraints with the edited ones; both expose the `id` read
  // here and satisfy `constraintDisablesPath`'s structural param.
  constraints: Array<{ id: string }>,
  editing: string | null
): PathsByObjectType {
  const filteredPaths: PathsByObjectType = {};
  for (const objectTypeName of Object.keys(paths)) {
    const filtered = paths[objectTypeName].filter((p) => {
      return !constraints.some(
        (c) =>
          c.id !== editing &&
          // `constraintDisablesPath` reads only `type` + a `secondaryPath`
          // that can test membership — the structural shape `EditedConstraint`
          // satisfies. Bridge to the helper's declared param shape.
          constraintDisablesPath(
            c as unknown as Parameters<typeof constraintDisablesPath>[0],
            p.elements
          )
      );
    });
    if (filtered.length > 1) {
      filteredPaths[objectTypeName] = filtered;
    }
  }
  return filteredPaths;
}

/**
 * Multiple propagation inspector
 *
 * Props:
 * - object: the object for the MPCs
 * - constraints: the list of MPCs
 * - ui: the MPCInspector ui store
 * - controller: the controller to use (an instance of EditObjectController)
 */
/** Props for the {@link MultiplePropagationConstraintsInspector} component. */
interface MultiplePropagationConstraintsInspectorProps {
  /** The object the MPCs belong to. */
  object: IObjectType;
  /** The list of MPCs. */
  constraints: IMultiplePropagationConstraint[];
  /** The MPCInspector ui store. */
  ui: MPCInspectorUI;
  /** The controller to use (an instance of EditObjectController). */
  controller: EditObjectController;
}

const MultiplePropagationConstraintsInspector = ({
  object,
  constraints,
  ui,
  controller,
}: MultiplePropagationConstraintsInspectorProps) => {
  const { editing, adding, selected, select, changes } = ui;
  const {
    onDelete,
    onUndoDelete,
    onStartEdit,
    onStopEdit,
    onAddMPC,
    onCancelAddMPC,
    onChangeMPC,
  } = useMPCActions(ui);

  const paths = useEditorPaths(controller, object);
  const editedConstraints = useEditedConstraints(ui, constraints, paths);

  const allConstraints: Array<{ id: string }> = (
    controller.availableMultiplePropagationConstraints.filter(
      (c) => c.objectType.id !== object.id
    ) as Array<{ id: string }>
  ).concat(editedConstraints);
  const filteredPaths = useFilteredPaths(paths, allConstraints, editing);

  if (editing != null) {
    const original = constraints.find((c) => c.id === editing);
    // The editing id always resolves to one of the edited constraints here.
    const editedConstraint = editedConstraints.find((c) => c.id === editing)!;
    return (
      <EditMultiplePropagationConstraintForm
        ui={ui}
        adding={adding}
        controller={controller}
        objectTypeName={object.name}
        paths={filteredPaths}
        constraint={editedConstraint}
        onChange={(key, value) => {
          onChangeMPC(
            editedConstraint.id!,
            key,
            // `original` is the MST constraint; reading the change key off it is
            // a string-valued lookup (only the type changes for existing MPCs).
            original != null
              ? (original as unknown as Record<ConstraintKey, string>)[key]
              : undefined,
            value
          );
        }}
        onRequestClose={onStopEdit}
        onRequestCancel={adding ? onCancelAddMPC : undefined}
      />
    );
  } else {
    return (
      <div className="multiple-propagation-constraints">
        <div className="add-mpc mb-[25px] flex justify-center">
          <IconButton className="icon-with-label" variant="inline" onClick={() => onAddMPC()}>
            <span>Add constraint</span>
            <AddPlus />
          </IconButton>
        </div>

        {editedConstraints.map((c) => (
          <MultiplePropagationConstraint
            key={c.id}
            constraint={c}
            onSelect={() => select(c.id)}
            selected={selected === c.id}
            deleted={changes.isRemoved(c.id)}
            onDelete={() => onDelete(c.id)}
            onUndoDelete={() => onUndoDelete(c.id)}
            editing={ui.isEditing(c.id)}
            onStartEdit={() => onStartEdit(c.id)}
            dirty={changes.isDirty(c.id)}
            paths={paths}
            added={c.added}
          />
        ))}
      </div>
    );
  }
};

// Wrap the view in observer
const ObservedMultiplePropagationConstraintsInspector = observer(MultiplePropagationConstraintsInspector);
export default ObservedMultiplePropagationConstraintsInspector;
